'use client';

import type { ReactNode } from 'react';
import HomeDashboardSkeleton from '~/app/(secured)/[workspaceId]/_components/Home/HomeDashboardSkeleton';
import { DashboardPageSkeleton } from '~/components/Navigation/DashboardPageSkeleton';
import { ListPageSkeleton } from '~/components/Navigation/ListPageSkeleton';
import { WorkspacePageSkeleton } from '~/components/Navigation/WorkspacePageSkeleton';
import { SettingsPageSkeleton } from '~/components/settings/SettingsPageSkeleton';
import { Routes } from '~/config';

function workspaceRelativePath(href: string): string {
  const pathOnly = href.split('?')[0]?.split('#')[0] ?? href;
  const segments = pathOnly.split('/').filter(Boolean);
  // /{workspaceId}/... → rest; bare /{workspaceId} → ''
  if (segments.length <= 1) return '';
  return `/${segments.slice(1).join('/')}`;
}

export function isSettingsHref(href: string): boolean {
  return workspaceRelativePath(href).startsWith(Routes.SETTINGS);
}

export function isHomeHref(href: string): boolean {
  return workspaceRelativePath(href) === '';
}

/** Settings-internal soft nav is handled by SettingsShell, not the workspace pane. */
export function isSettingsInternalNavigation(
  pathname: string | null,
  pendingHref: string,
): boolean {
  if (!pathname) return false;
  return isSettingsHref(pathname) && isSettingsHref(pendingHref);
}

export function getPendingRouteSkeleton(pendingHref: string): ReactNode {
  const relative = workspaceRelativePath(pendingHref);

  if (relative === '') {
    return <HomeDashboardSkeleton />;
  }

  if (relative.startsWith(Routes.SETTINGS)) {
    return (
      <div className="bg-settings-canvas flex h-full min-h-full w-full flex-1 flex-col overflow-hidden">
        <div className="mx-auto w-full max-w-4xl px-6 py-8 md:px-8">
          <SettingsPageSkeleton />
        </div>
      </div>
    );
  }

  if (
    relative.startsWith(Routes.CONTENT_SCANNER) ||
    relative.startsWith(Routes.COMMUNICATION_REVIEW) ||
    relative.startsWith(Routes.INFLUENCER_MONITORING) ||
    relative.startsWith(Routes.EMPLOYEE_REVIEW) ||
    relative.startsWith(Routes.SELF_REPORTING)
  ) {
    return <DashboardPageSkeleton />;
  }

  if (
    relative.startsWith(Routes.PROJECTS) ||
    relative.startsWith(Routes.DOCUMENTS) ||
    relative.startsWith(Routes.CLAIMS_APPROVAL) ||
    relative.startsWith(Routes.NOTIFICATIONS) ||
    relative.startsWith(Routes.REMEDIATION) ||
    relative.startsWith(Routes.ARCHIVE_QUEUE) ||
    relative.startsWith(Routes.DECISION_STORE) ||
    relative.startsWith(Routes.POLICY_ENGINE)
  ) {
    return <ListPageSkeleton />;
  }

  return <WorkspacePageSkeleton />;
}
